import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Palette, Type } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { AVAILABLE_FONTS } from '@/context/ThemeContext';
import { ColorPickerField } from '@/components/admin/site-settings/ColorPickerField';

const COLOR_FIELDS = [
  ['theme_primary', 'colorPrimary'],
  ['theme_background', 'colorBackground'],
  ['theme_foreground', 'colorText'],
  ['theme_card', 'colorCard'],
  ['theme_card_foreground', 'colorCardText'],
  ['theme_secondary', 'colorSecondary'],
  ['theme_muted', 'colorMuted'],
  ['theme_muted_foreground', 'colorMutedText'],
  ['theme_border', 'colorBorder'],
  ['theme_primary_foreground', 'colorPrimaryText'],
];

export const ThemeColorsCard = ({ settings, updateField }) => {
  const { t } = useLanguage();

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Palette className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-bold">{t('siteSettings.themeColors')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">{t('siteSettings.themeColorsHint')}</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {COLOR_FIELDS.map(([field, labelKey]) => (
            <ColorPickerField
              key={field}
              label={t(`siteSettings.${labelKey}`)}
              value={settings[field]}
              onChange={(v) => updateField(field, v)}
              testId={field.replace(/_/g, '-')}
            />
          ))}
        </div>

        <Separator className="my-6" />

        <div className="flex items-center gap-2 mb-4">
          <Type className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-bold">{t('siteSettings.fonts')}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            ['theme_font_body', 'fontBody', 'font-body-select'],
            ['theme_font_heading', 'fontHeading', 'font-heading-select'],
          ].map(([field, labelKey, testId]) => (
            <div key={field}>
              <Label className="text-sm">{t(`siteSettings.${labelKey}`)}</Label>
              <Select
                value={settings[field] || 'default'}
                onValueChange={(v) => updateField(field, v === 'default' ? '' : v)}
              >
                <SelectTrigger className="bg-secondary border-border mt-1" data-testid={testId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-[280px]">
                  <SelectItem value="default">{t('siteSettings.fontDefault')} (Manrope)</SelectItem>
                  {AVAILABLE_FONTS.map((font) => (
                    <SelectItem key={font} value={font}>{font}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
